
import * as THREE from "../../../libs/three.js/build/three.module.js";

// True constant-XY-offset polyline construction, for the Parallelforskydning
// tool (see tools/parallelOffsetTool.js) - part of the same "Perpendicular /
// Offset geometry" family as perpendicularConstraint.js, but deliberately a
// separate function rather than folded into it: offsetting a whole polyline
// by one constant distance (this file) and constraining a single point
// perpendicular to the nearest segment (perpendicularConstraint.js) solve
// different problems, and forcing them into one function would only make
// both harder to follow. They do share the same XY/signed-distance
// convention (see computeSignedOffsetToBaseline() in that file) so a single
// cursor-driven "side + distance" value feeds both naturally.
//
// Algorithm: each segment is offset perpendicular to its own direction by
// the same signed distance `d` (so every offset segment stays exactly
// parallel to, and at planimetric distance |d| from, its original segment -
// this is NOT offsetVertex = originalVertex + localNormal*d, which is
// generally wrong at a bend: a vertex has no single normal, only its two
// adjacent segments do). At each interior vertex, the two adjacent offset
// segments are joined by intersecting their offset lines (a "miter" join) -
// the standard, geometrically exact way to keep a true constant offset
// through a bend, convex or concave alike, with no special-casing needed
// for which kind of corner it is.
//
// Miter length is bounded by a limit exactly the way SVG/CSS stroke
// rendering already defines it (the 'stroke-miterlimit' property, default
// 4): if the intersection point ends up further from the original vertex
// than `miterLimit * |d|`, a bevel (the straight segment between the two
// offset segments' own endpoints) is used instead - not an invented
// threshold, but the same industry-standard mechanism most vector graphics/
// CAD systems already use for exactly this problem (a mitered join's tip
// length, as a multiple of the offset/stroke distance, beyond which the
// spike becomes unreasonable at acute corners). The same bevel fallback
// also covers the (rarer) case where the two offset lines are parallel and
// never intersect at all (a straight continuation, or an exact 180-degree
// reversal).
//
// Z is never interpolated or taken from a pick: each result vertex keeps
// exactly the Z of its corresponding original baseline vertex (the shared
// vertex, for interior miter/bevel joins) - see this module's own doc
// comment in parallelOffsetTool.js's usage for why.
//
// KNOWN LIMITATION (see task's own explicit allowance for this): this
// function does not detect or repair self-intersections between
// *non-adjacent* offset segments - a large offset relative to a tight
// concave run of bends can still produce a locally-valid (no NaN, every
// segment correctly parallel/at-distance) but globally self-crossing
// result. Only local degeneracy (a zero-length input segment, or an
// individual join producing a non-finite point) is detected and reported;
// full self-intersection cleanup is a substantially larger, separate
// problem left for a later iteration.

// SVG 1.1 / CSS Fill and Stroke Module Level 3's own default for
// 'stroke-miterlimit' - see this module's doc comment above.
export const DEFAULT_MITER_LIMIT = 4;

const EPS = 1e-9;

function flattenXY(p){
	return new THREE.Vector2(p.x, p.y);
}

// Intersection of two infinite 2D lines, each given as a point + direction.
// Returns null if the lines are (near-)parallel (including exactly
// collinear/coincident).
function intersectLines2D(p1, d1, p2, d2){
	const denom = d1.x * d2.y - d1.y * d2.x;
	if(Math.abs(denom) < EPS) return null;

	const dx = p2.x - p1.x;
	const dy = p2.y - p1.y;
	const t = (dx * d2.y - dy * d2.x) / denom;

	return new THREE.Vector2(p1.x + d1.x * t, p1.y + d1.y * t);
}

// Offsets a 3D baseline polyline (>=2 points) by a constant planimetric (XY)
// distance. The SIGN of `signedDistance` selects the side, using the same
// convention as computeSignedOffsetToBaseline() in perpendicularConstraint.js
// (perp = (-dir.y, dir.x) of the relevant segment's direction) - a positive
// value offsets to that rotation's side, negative to the other, consistently
// across every segment of the whole polyline (never per-segment-independent
// - that's what makes this a true constant offset, not per-vertex
// displacement).
//
// Returns either:
//   {status: "ok", points: THREE.Vector3[], joins: string[]}
//     - `points` has the same length as the input; `joins` has length
//       points.length-2 (one entry per interior vertex), each "miter",
//       "bevel-miter-limit" (miter length exceeded the limit), or
//       "bevel-parallel" (adjacent offset lines never intersect).
//   {status: "invalid", reason: "too-few-points" | "degenerate-segment" | "non-finite-join", ...}
//     - never NaN/Infinity in a returned point; the caller should treat this
//       as "no valid offset for this input", not attempt to render it.
export function offsetPolyline(points, signedDistance, options = {}){
	const miterLimit = options.miterLimit ?? DEFAULT_MITER_LIMIT;

	if(!points || points.length < 2){
		return {status: "invalid", reason: "too-few-points"};
	}

	const d = signedDistance;
	if(Math.abs(d) < EPS){
		// zero offset: the "offset" polyline is just the baseline itself
		return {status: "ok", points: points.map((p) => p.clone()), joins: points.slice(1, -1).map(() => "zero-offset")};
	}

	const segments = [];
	for(let i = 0; i < points.length - 1; i++){
		const a = flattenXY(points[i]);
		const b = flattenXY(points[i + 1]);
		const dir = new THREE.Vector2(b.x - a.x, b.y - a.y);
		const len = dir.length();
		if(len < EPS){
			return {status: "invalid", reason: "degenerate-segment", segmentIndex: i};
		}
		dir.divideScalar(len);

		const perp = new THREE.Vector2(-dir.y, dir.x);
		const offsetA = new THREE.Vector2(a.x + perp.x * d, a.y + perp.y * d);
		const offsetB = new THREE.Vector2(b.x + perp.x * d, b.y + perp.y * d);
		segments.push({offsetA, offsetB, dir, perp});
	}

	const resultPoints = [new THREE.Vector3(segments[0].offsetA.x, segments[0].offsetA.y, points[0].z)];
	const joins = [];

	for(let i = 0; i < segments.length - 1; i++){
		const segA = segments[i];
		const segB = segments[i + 1];
		const originalVertex = points[i + 1];
		const originalXY = flattenXY(originalVertex);

		const intersection = intersectLines2D(segA.offsetA, segA.dir, segB.offsetA, segB.dir);

		let joinXY;
		let joinType;

		if(!intersection){
			joinXY = segA.offsetB.clone().lerp(segB.offsetA, 0.5);
			joinType = "bevel-parallel";
		}else{
			const miterLength = intersection.distanceTo(originalXY);
			const miterRatio = miterLength / Math.abs(d);

			if(!isFinite(miterRatio) || miterRatio > miterLimit){
				joinXY = segA.offsetB.clone().lerp(segB.offsetA, 0.5);
				joinType = "bevel-miter-limit";
			}else{
				joinXY = intersection;
				joinType = "miter";
			}
		}

		if(!isFinite(joinXY.x) || !isFinite(joinXY.y)){
			return {status: "invalid", reason: "non-finite-join", vertexIndex: i + 1};
		}

		resultPoints.push(new THREE.Vector3(joinXY.x, joinXY.y, originalVertex.z));
		joins.push(joinType);
	}

	const lastSeg = segments[segments.length - 1];
	resultPoints.push(new THREE.Vector3(lastSeg.offsetB.x, lastSeg.offsetB.y, points[points.length - 1].z));

	return {status: "ok", points: resultPoints, joins};
}
